import { create } from 'zustand'
import type { Giveaway } from '@/models/giveaway'

export interface PendingEntry {
  giveawayId: number
  objectiveId: number
}

const PENDING_ENTRY_KEY = 'jlm:pending-entry'

function readPendingEntry(): PendingEntry | null {
  try {
    const raw = sessionStorage.getItem(PENDING_ENTRY_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as PendingEntry).giveawayId === 'number' &&
      typeof (parsed as PendingEntry).objectiveId === 'number'
    ) {
      return parsed as PendingEntry
    }
  } catch {
    /* storage unavailable or malformed */
  }
  return null
}

function writePendingEntry(entry: PendingEntry | null): void {
  try {
    if (entry === null) sessionStorage.removeItem(PENDING_ENTRY_KEY)
    else sessionStorage.setItem(PENDING_ENTRY_KEY, JSON.stringify(entry))
  } catch {
    /* the intent simply will not survive a reload */
  }
}

/**
 * The Quasar MainLayout kept six independent booleans (`auth`, `giveaway`,
 * `my_media`, `my_loot`, `my_profile`, `my_giveaways`) and every child emitted
 * a bespoke close event up to it. Modelled here as one exclusive stack so that
 * opening a second overlay closes the first, Escape always pops the top one,
 * and nested overlays (MyProfile -> MyMedia) restore correctly.
 */
/**
 * Only the transient flows are overlays. The account screens (loot, media,
 * profile, giveaways) are pages at `/my/*` — they are destinations people link
 * to and scroll, which a modal serves badly.
 */
export type OverlayName = 'auth' | 'new-giveaway' | 'edit-giveaway' | 'giveaway-details'

interface OverlayState {
  stack: OverlayName[]
  /** Draft carried into the new-giveaway wizard when resuming after OAuth. */
  draftGiveaway: Giveaway | null
  /** Giveaway shown by the details overlay. */
  selectedGiveaway: Giveaway | null
  /** Giveaway open in the edit overlay. */
  editingGiveaway: Giveaway | null
  /**
   * An entry a signed-out visitor asked for. Clicking an objective opens the
   * auth overlay; this remembers what they were trying to do so it can be
   * finished the moment they are signed in, instead of dropping the intent and
   * making them find the button again.
   *
   * Mirrored into sessionStorage so it also survives the Discord OAuth round
   * trip, which unloads the page entirely.
   */
  pendingEntry: PendingEntry | null

  open: (name: OverlayName) => void
  close: (name?: OverlayName) => void
  closeAll: () => void
  isOpen: (name: OverlayName) => boolean
  setDraftGiveaway: (giveaway: Giveaway | null) => void
  setSelectedGiveaway: (giveaway: Giveaway | null) => void
  /** Opens the edit overlay on `giveaway` in one step. */
  editGiveaway: (giveaway: Giveaway) => void
  setPendingEntry: (entry: PendingEntry | null) => void
}

export const useOverlayStore = create<OverlayState>((set, get) => ({
  stack: [],
  draftGiveaway: null,
  selectedGiveaway: null,
  editingGiveaway: null,
  pendingEntry: readPendingEntry(),

  open: (name) =>
    set((state) =>
      state.stack.includes(name) ? state : { stack: [...state.stack, name] },
    ),

  close: (name) =>
    set((state) => ({
      stack: name ? state.stack.filter((entry) => entry !== name) : state.stack.slice(0, -1),
    })),

  closeAll: () => set({ stack: [] }),

  isOpen: (name) => get().stack.includes(name),

  setDraftGiveaway: (draftGiveaway) => set({ draftGiveaway }),
  setSelectedGiveaway: (selectedGiveaway) => set({ selectedGiveaway }),

  setPendingEntry: (pendingEntry) => {
    writePendingEntry(pendingEntry)
    set({ pendingEntry })
  },

  editGiveaway: (giveaway) =>
    set((state) => ({
      editingGiveaway: giveaway,
      stack: state.stack.includes('edit-giveaway')
        ? state.stack
        : [...state.stack, 'edit-giveaway'],
    })),
}))

/** The overlay currently on top, or null. Escape and focus trapping use this. */
export const selectTopOverlay = (state: OverlayState): OverlayName | null =>
  state.stack.at(-1) ?? null
